export type CaptureStatus = 'idle' | 'requesting' | 'selected' | 'connecting' | 'active' | 'interrupted' | 'error'
export type CaptureMediaState = 'idle' | 'playing' | 'waiting' | 'stalled' | 'paused' | 'muted' | 'unmuted'
export type CaptureFailureKind = 'permission-denied' | 'source-unavailable' | 'timeout' | 'unsupported' | 'unknown'

export interface CaptureInfo {
  readonly width: number
  readonly height: number
  readonly label: string
}

interface ExtendedDisplayMediaStreamOptions extends DisplayMediaStreamOptions {
  readonly monitorTypeSurfaces?: 'include' | 'exclude'
  readonly selfBrowserSurface?: 'include' | 'exclude'
  readonly surfaceSwitching?: 'include' | 'exclude'
}

const DISPLAY_CAPTURE_OPTIONS: ExtendedDisplayMediaStreamOptions = {
  video: { displaySurface: 'window', frameRate: { ideal: 10 } },
  audio: false,
  monitorTypeSurfaces: 'include',
  selfBrowserSurface: 'exclude',
  surfaceSwitching: 'exclude',
}

export function classifyCaptureFailure(error: unknown): CaptureFailureKind {
  const name = typeof error === 'object' && error !== null && 'name' in error
    ? String(error.name)
    : ''
  const message = error instanceof Error ? error.message : String(error)

  if (name === 'NotAllowedError' || /permission denied|not allowed by the user|permission dismissed/i.test(message)) {
    return 'permission-denied'
  }
  if (name === 'NotSupportedError' || name === 'SecurityError' || /display capture is not supported/i.test(message)) {
    return 'unsupported'
  }
  if (name === 'NotReadableError'
    || name === 'NotFoundError'
    || name === 'AbortError'
    || name === 'OverconstrainedError'
    || /could not start (?:video )?source|starting (?:video|screen).*failed|failed to (?:start|allocate).*video|video source.*(?:unavailable|busy|in use)/i.test(message)) {
    return 'source-unavailable'
  }
  if (/等待捕获画面超时|capture frame timed out|timed out waiting for capture/i.test(message)) {
    return 'timeout'
  }
  return 'unknown'
}

export class CaptureService extends EventTarget {
  readonly video: HTMLVideoElement
  private stream: MediaStream | null = null
  private selectionRevision = 0
  status: CaptureStatus = 'idle'
  mediaState: CaptureMediaState = 'idle'
  lastError: string | null = null

  constructor() {
    super()
    this.video = document.createElement('video')
    this.video.muted = true
    this.video.playsInline = true
    this.video.autoplay = true
    this.video.addEventListener('playing', () => {
      if (this.stream) this.setMediaState('playing')
    })
    this.video.addEventListener('waiting', () => {
      if (this.stream) this.setMediaState('waiting')
    })
    this.video.addEventListener('stalled', () => {
      if (this.stream) this.setMediaState('stalled')
    })
    this.video.addEventListener('pause', () => {
      if (this.stream) this.setMediaState('paused')
    })
  }

  get supported(): boolean {
    return typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function'
  }

  async selectWindow(): Promise<CaptureInfo> {
    this.stop()
    const selectionRevision = ++this.selectionRevision
    this.lastError = null
    this.setStatus('requesting')
    let requestedStream: MediaStream | null = null
    try {
      if (!navigator.mediaDevices?.getDisplayMedia) {
        throw new DOMException('Display capture is not supported by this browser', 'NotSupportedError')
      }
      const stream = await navigator.mediaDevices.getDisplayMedia(DISPLAY_CAPTURE_OPTIONS)
      requestedStream = stream
      if (selectionRevision !== this.selectionRevision) {
        this.discardStream(stream)
        throw new DOMException('Capture selection was replaced', 'AbortError')
      }
      const track = stream.getVideoTracks()[0]
      if (!track) throw new Error('未获得视频轨道')
      this.stream = stream
      track.addEventListener('ended', () => {
        if (this.stream !== stream) return
        this.setStatus('interrupted')
        this.dispatchEvent(new Event('ended'))
      }, { once: true })
      track.addEventListener('mute', () => {
        if (this.stream === stream) this.setMediaState('muted')
      })
      track.addEventListener('unmute', () => {
        if (this.stream === stream) this.setMediaState('unmuted')
      })
      const settings = track.getSettings()
      this.setStatus('selected')
      return {
        width: settings.width || 0,
        height: settings.height || 0,
        label: track.label || 'MapleStory',
      }
    } catch (error) {
      this.discardStream(requestedStream)
      if (selectionRevision !== this.selectionRevision) throw error
      this.setMediaState('idle')
      this.lastError = error instanceof Error ? `${error.name}: ${error.message}` : String(error)
      this.setStatus('error')
      throw error
    }
  }

  async connectSelectedWindow(): Promise<CaptureInfo> {
    const selectionRevision = this.selectionRevision
    const stream = this.stream
    const track = stream?.getVideoTracks()[0]
    if (!stream || !track || track.readyState === 'ended') {
      throw new DOMException('Selected video source is no longer available', 'NotReadableError')
    }
    if (this.status === 'active') return this.captureInfo(track)

    this.lastError = null
    this.setStatus('connecting')
    try {
      this.video.srcObject = stream
      await this.video.play()
      await this.waitForDimensions()
      if (this.stream !== stream || String(track.readyState) === 'ended') {
        throw new DOMException('Selected video source ended while connecting', 'NotReadableError')
      }
      this.setStatus('active')
      this.setMediaState(track.muted ? 'muted' : 'playing')
      return this.captureInfo(track)
    } catch (error) {
      this.discardStream(stream)
      if (selectionRevision !== this.selectionRevision) throw error
      this.setMediaState('idle')
      this.lastError = error instanceof Error ? `${error.name}: ${error.message}` : String(error)
      this.setStatus('error')
      throw error
    }
  }

  get mediaStream(): MediaStream | null {
    return this.stream
  }

  async snapshot(): Promise<ImageBitmap> {
    if (this.status !== 'active' || this.video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) throw new Error('捕获画面尚未就绪')
    return createImageBitmap(this.video)
  }

  stop(): void {
    this.selectionRevision += 1
    const stream = this.stream
    this.stream = null
    for (const track of stream?.getTracks() ?? []) track.stop()
    this.video.srcObject = null
    this.setMediaState('idle')
    if (this.status !== 'idle') this.setStatus('idle')
  }

  private setStatus(status: CaptureStatus): void {
    this.status = status
    this.dispatchEvent(new CustomEvent('status', { detail: status }))
  }

  private setMediaState(state: CaptureMediaState): void {
    if (this.mediaState === state) return
    this.mediaState = state
    this.dispatchEvent(new CustomEvent('media-state', { detail: state }))
  }

  private discardStream(stream: MediaStream | null): void {
    if (!stream) return
    if (this.stream === stream) this.stream = null
    if (this.video.srcObject === stream) this.video.srcObject = null
    for (const track of stream.getTracks()) track.stop()
  }

  private captureInfo(track: MediaStreamTrack): CaptureInfo {
    const settings = track.getSettings()
    return {
      width: this.video.videoWidth || settings.width || 0,
      height: this.video.videoHeight || settings.height || 0,
      label: track.label || 'MapleStory',
    }
  }

  private async waitForDimensions(): Promise<void> {
    if (this.video.videoWidth && this.video.videoHeight) return
    await new Promise<void>((resolve, reject) => {
      const handleLoadedMetadata = () => {
        cleanup()
        resolve()
      }
      const timer = window.setTimeout(() => {
        cleanup()
        reject(new Error('等待捕获画面超时'))
      }, 8_000)
      const cleanup = () => {
        window.clearTimeout(timer)
        this.video.removeEventListener('loadedmetadata', handleLoadedMetadata)
      }
      this.video.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true })
    })
  }
}

export const captureService = new CaptureService()
